import { createFileRoute } from "@tanstack/react-router"
import { Check, X, Zap, AlertCircle, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { LifetimeAccessPricing } from "@/components/life-time-access-pricing"
import { createLLMOMeta, createBreadcrumbSchema, createFAQSchema, createArticleSchema } from "@/lib/seo"
import { useAuth } from "@/components/auth/AuthProvider"
import { useAnalytics } from "@/lib/analytics"
import { Paragraph, ContentSection } from "@/components/tools/SeoContent"
import { useState } from "react"
import { AskAIExplain } from "@/components/ask-ai-to-explain"

// ─── FAQ Data ────────────────────────────────────────────────
const faqs = [
  {
    question: "Is Zapify better than InstantDM?",
    answer:
      "For Instagram-first creators, yes. Zapify offers a $0 free plan for 30 days or 1K contacts, AI-powered replies with brand voice controls, bulk DM automation, Story DMs, follower and lead attribution, templates, smart offers, 10+ language support, and a possible $200 lifetime access offer. InstantDM may fit users who specifically need scheduling, Facebook automation, API access, viral mode, or safety queues.",
  },
  {
    question: "What is the best InstantDM alternative?",
    answer:
      "Zapify is a strong InstantDM alternative for creators who want AI-powered Instagram automation, bulk DMs, detailed insights, followers and leads gained through automation, ask-to-follow flows, email capture, templates, smart offers, and a $0 free plan for 30 days or 1K contacts.",
  },
  {
    question: "Why switch from InstantDM to Zapify?",
    answer:
      "Switch to Zapify if you want a simpler Instagram growth platform with AI replies, bulk DM workflows, Zapify insights, smart offers, templates, language switching, 30-day free-plan value, and a possible lifetime access offer. InstantDM is feature-heavy but can feel more operational than necessary for creators.",
  },
  {
    question: "Does Zapify have unlimited DMs?",
    answer:
      "Yes. Zapify's paid plans include unlimited DMs and unlimited automations. There are no monthly caps. InstantDM's Legend Pro lists Unlimited (750/hr) and Trendsetter lists Truly Unlimited.",
  },
  {
    question: "Does InstantDM have a free plan?",
    answer:
      "InstantDM's public pricing page promotes a 7-day free trial with no credit card required. A $0 30-day free plan was not clearly shown. Zapify offers a $0 free plan for 30 days or 1K contacts.",
  },
  {
    question: "Does Zapify have AI replies?",
    answer:
      "Yes. Zapify is an AI-powered Instagram comment management and automation platform with AI replies and brand voice controls that match your tone, length, and style.",
  },
  {
    question: "Does InstantDM have AI replies?",
    answer:
      "Yes. InstantDM publicly promotes Instagram AI Agents for DMs and comments. Zapify differentiates with creator-growth attribution, smart offers, templates, language switching, and free-plan/lifetime economics.",
  },
  {
    question: "How does Zapify pricing compare with InstantDM?",
    answer:
      "Zapify has a $0 free plan for 30 days or 1K contacts, paid plans at $9.99/month and $19.99/month, and a limited $200 lifetime access offer. InstantDM publicly lists Legend Pro at $9.99/month and Trendsetter at $24.99/month, plus a 7-day free trial. Zapify has the stronger value story with the 30-day free plan and lifetime offer.",
  },
  {
    question: "Who should still consider InstantDM?",
    answer:
      "InstantDM may still make sense if you specifically want scheduling with AutoDM, Facebook automation, Meta ads automation, API/webhook access, viral mode, safety queues, or app integrations. For most Instagram-first creators who want growth attribution and smart campaign setup, Zapify is the better fit.",
  },
]

// ─── Feature Table Data ──────────────────────────────────────
type FeatureRow = {
  name: string
  zapify: string | boolean
  instantdm: string | boolean
  zapifyNote?: string
  instantdmNote?: string
}

type FeatureCategory = {
  category: string
  rows: FeatureRow[]
}

const featureCategories: FeatureCategory[] = [
  {
    category: "Instagram automation",
    rows: [
      { name: "Instagram comment-to-DM automation", zapify: true, instantdm: true, zapifyNote: "Personalized DMs with links, offers, and content" },
      { name: "Bulk DM automation", zapify: true, instantdm: true, zapifyNote: "Unlimited DMs on paid plans", instantdmNote: "Legend: 750/hr cap" },
      { name: "Unlimited DMs on paid plans", zapify: true, instantdm: true, zapifyNote: "No cap", instantdmNote: "Trendsetter: Truly Unlimited" },
      { name: "Post and Reel automation", zapify: true, instantdm: true },
      { name: "Multiple selected posts", zapify: true, instantdm: false, instantdmNote: "Not publicly confirmed" },
      { name: "Any-post automation", zapify: true, instantdm: false, zapifyNote: "Evergreen automation", instantdmNote: "Universal triggers listed; verify parity" },
      { name: "Story DM automation", zapify: true, instantdm: true },
      { name: "Facebook automation", zapify: false, instantdm: true, zapifyNote: "Not a Zapify focus" },
      { name: "Meta ads comment automation", zapify: false, instantdm: true, zapifyNote: "Not publicly confirmed" },
      { name: "Scheduling with AutoDM", zapify: false, instantdm: true, zapifyNote: "Not publicly confirmed" },
    ],
  },
  {
    category: "AI, growth, and conversion",
    rows: [
      { name: "AI-powered comment replies", zapify: true, instantdm: true, zapifyNote: "Built-in contextual AI", instantdmNote: "AI Agents" },
      { name: "Brand voice controls", zapify: true, instantdm: false, zapifyNote: "Tone, length, style", instantdmNote: "Not clearly promoted" },
      { name: "Ask-to-follow before sharing content", zapify: true, instantdm: true, zapifyNote: "Native growth flow" },
      { name: "Email capture in DMs", zapify: true, instantdm: true },
      { name: "Phone number collection", zapify: false, instantdm: true, zapifyNote: "Not publicly confirmed" },
      { name: "Detailed Instagram Reel insights", zapify: true, instantdm: false, instantdmNote: "Advanced dashboards on Trendsetter only" },
      { name: "Followers gained attribution", zapify: true, instantdm: false, instantdmNote: "No public equivalent" },
      { name: "Leads gained attribution", zapify: true, instantdm: false, instantdmNote: "No public equivalent" },
    ],
  },
  {
    category: "Setup, accounts, and business model",
    rows: [
      { name: "Templates for quick setup", zapify: true, instantdm: false, zapifyNote: "Launch in minutes", instantdmNote: "Not clearly promoted" },
      { name: "Smart offers / link cards in DMs", zapify: true, instantdm: false, zapifyNote: "Quick campaign setup", instantdmNote: "Button links listed" },
      { name: "Multiple Instagram accounts", zapify: true, instantdm: false, zapifyNote: "Up to 6 profiles", instantdmNote: "Not clearly listed" },
      { name: "Team access", zapify: true, instantdm: false, zapifyNote: "From Starter", instantdmNote: "Not publicly confirmed" },
      { name: "10+ language switching", zapify: true, instantdm: false, instantdmNote: "Not publicly confirmed" },
      { name: "Lifetime access option", zapify: true, instantdm: false, zapifyNote: "$200 one-time" },
      { name: "API / webhooks", zapify: false, instantdm: true, zapifyNote: "Not core Zapify positioning", instantdmNote: "Trendsetter plan" },
      { name: "Viral mode / safety queues", zapify: false, instantdm: true, zapifyNote: "Not publicly confirmed" },
      { name: "Meta approval / partner status", zapify: true, instantdm: true, zapifyNote: "Approved by Meta", instantdmNote: "Meta Business Partner" },
    ],
  },
]

// ─── Pricing Data ────────────────────────────────────────────
const pricingTiers = [
  { label: "Free / trial", zapify: "$0 for 30 days", instantdm: "$0 for 7 days", note: "Zapify gives 4x more trial time" },
  { label: "Entry paid plan", zapify: "$9.99/mo", instantdm: "$9.99/mo", note: "Same price, but Zapify includes growth attribution" },
  { label: "Professional", zapify: "$19.99/mo", instantdm: "$24.99/mo", note: "Zapify is $5/mo cheaper with unlimited AI replies" },
  { label: "Lifetime access", zapify: "$200 once", instantdm: "Not available", note: "Zapify offers a one-time option with no recurring fees" },
]

const problemCards = [
  {
    title: "InstantDM only gives a 7-day free trial",
    body:
      "7 days is not enough to properly test a DM automation strategy. You need time to set up, run a campaign, and see real results. Zapify gives you 30 days or 1K contacts - enough time to validate before paying.",
  },
  {
    title: "InstantDM is built like an operations stack",
    body:
      "Scheduling, Facebook automation, API access, webhooks, viral mode, safety queues - useful for agencies, but overwhelming for creators who just want comments to become DMs, leads, and followers.",
  },
  {
    title: "InstantDM lacks clear growth attribution",
    body:
      "InstantDM can automate DMs, but it does not publicly show you which campaigns grew your followers or captured leads. Zapify tracks what the automation produced, not just what it sent.",
  },
]

const chooseZapifyIf = [
  "Want a $0 free plan for 30 days or 1K contacts - not just 7 days",
  "Want AI-powered replies with brand voice controls",
  "Want unlimited DMs on paid plans without hourly caps",
  "Want to know which campaigns grew your followers and captured leads",
  "Want templates and smart offers for faster campaign setup",
  "Want Story DM automation covering all Instagram touchpoints",
  "Want ask-to-follow flows that convert commenters into followers first",
  "Want 10+ language support for global audiences",
  "Want to lock in lifetime access for $200 instead of paying monthly forever",
]

const chooseInstantDMIf = [
  "Need scheduling with AutoDM attached to posts",
  "Need Facebook comment and Messenger automation",
  "Need Meta ads comment automation and click-to-DM ads",
  "Need API access, webhooks, or external integrations",
  "Need viral mode, safety queues, or advanced account safety",
  "Need Trendsetter plan features like WhatsApp support",
]

const differentiators = [
  {
    num: "01",
    title: "30-day free plan vs 7-day trial",
    body:
      "InstantDM gives you a week to test. Zapify gives you 30 days or 1K contacts - enough time to run a real campaign, see actual results, and decide if the tool works for your growth.",
  },
  {
    num: "02",
    title: "Creator growth platform, not an operations stack",
    body:
      "InstantDM has scheduling, API access, viral mode, and Facebook automation. Zapify strips away operational complexity and focuses on the creator growth loop: comments to DMs to followers to revenue.",
  },
  {
    num: "03",
    title: "Growth attribution you can act on",
    body:
      "Zapify tracks followers gained and leads captured through automation. You know which campaigns actually grew your account, not just which ones sent messages.",
  },
  {
    num: "04",
    title: "Smart offers and templates for faster launches",
    body:
      "Send a free resource, product page, booking link, coupon, or lead magnet from comments in minutes. Templates mean less setup time for every campaign.",
  },
  {
    num: "05",
    title: "Brand voice AI that sounds like you",
    body:
      "Zapify's AI understands context and matches your tone, length, emoji usage, and style. Replies feel personal, not robotic.",
  },
  {
    num: "06",
    title: "Lifetime access for cleaner economics",
    body:
      "Zapify offers a limited $200 lifetime access option. InstantDM's Trendsetter is $24.99/month recurring. For creators who want long-term value, Zapify's one-time payment is hard to beat.",
  },
]

// ─── Route ───────────────────────────────────────────────────
export const Route = createFileRoute("/comparison/instantdm-vs-zapify-pro")({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: "InstantDM vs Zapify: Best InstantDM Alternative for Instagram DM Automation",
        description:
          "Compare InstantDM vs Zapify for Instagram DM automation, AI replies, bulk DMs, Story DMs, pricing, insights, and creator growth. See why creators choose Zapify.",
        url: "https://zapify.pro/comparison/instantdm-vs-zapify-pro",
        publishedTime: "2026-06-06",
        modifiedTime: "2026-06-06",
        ogType: "article",
      }),
      { property: "og:image", content: "https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png" },
      { property: "og:locale", content: "en_US" },
      { name: "keywords", content: "InstantDM vs Zapify, InstantDM alternative, Zapify vs InstantDM, Instagram DM automation tool, Instagram comment to DM automation, bulk Instagram DM automation, AI Instagram replies, Story DM automation" },
    ],
    links: [
      { rel: "canonical", href: "https://zapify.pro/comparison/instantdm-vs-zapify-pro" },
    ],
    scripts: [
      createBreadcrumbSchema({
        itemListElement: [
          { position: 1, name: "Home", item: "https://zapify.pro" },
          { position: 2, name: "Comparisons", item: "https://zapify.pro/comparison" },
          { position: 3, name: "InstantDM vs Zapify", item: "https://zapify.pro/comparison/instantdm-vs-zapify-pro" },
        ],
      }),
      createFAQSchema(faqs),
      createArticleSchema({
        headline: "InstantDM vs Zapify: Best InstantDM Alternative for Instagram DM Automation",
        description: "A practical comparison of InstantDM and Zapify for Instagram DM automation, bulk DMs, AI replies, Story DMs, pricing, and creator growth.",
        image: "https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png",
        author: { name: "Zapify", url: "https://zapify.pro" },
        datePublished: "2026-06-06",
        dateModified: "2026-06-06",
        publisher: {
          name: "Zapify Pro",
          url: "https://zapify.pro",
          logo: "https://zapify.pro/logo-small.png",
          description: "AI-Powered Instagram Comment Management & Automation",
        },
      }),
    ],
  }),
  component: InstantDMComparisonPage,
})

// ─── Page Component ──────────────────────────────────────────
export function InstantDMComparisonPage() {
  const { track } = useAnalytics()
  const { openAuth } = useAuth()
  const [openFaq, setOpenFaq] = useState(0)

  const handleCTA = (ctaId: string, buttonText: string) => {
    track("cta_clicked", { cta_id: ctaId, button_text: buttonText })
    openAuth()
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-white dark:bg-black transition-colors duration-300">
      {/* Radial fade background */}
      <div className="absolute inset-0 bg-white/50 dark:bg-black/20 [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />
      {/* Dotted background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle,#80808015_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" />

      <div className="relative z-10 pt-28 sm:pt-36 pb-12">
        {/* Section 1: Hero */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 text-center mb-16 sm:mb-20">
          <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 shadow-sm mb-6">
            <span className="text-neutral-600 dark:text-neutral-300 font-semibold text-xs">Updated June 2026</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-900 dark:text-white mb-4 sm:mb-6 tracking-tight max-w-5xl mx-auto">
            <span className="gradient-text">Zapify vs</span> InstantDM
          </h1>
          <p className="text-xl sm:text-2xl md:text-3xl font-bold text-neutral-900 dark:text-white max-w-4xl mx-auto mb-5 tracking-tight">
            InstantDM is complex. Zapify is built for creator growth.
          </p>
          <p className="text-base sm:text-lg md:text-xl text-neutral-600 dark:text-neutral-400 font-medium max-w-3xl mx-auto mb-8 sm:mb-10">
            Zapify gives you AI-powered replies, growth attribution, smart offers, and a 30-day free plan. No scheduling tools, API keys, or viral mode configs needed.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button
              onClick={() => handleCTA("CTA_InstantDM_Hero", "Start Free 30 Days")}
              className="bg-primary-gradient text-primary-foreground hover:brightness-[0.97] active:brightness-[0.92] font-bold px-8 py-6 text-base rounded-full shadow-lg"
            >
              Start Free 30 Days
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="px-8 py-6 text-base rounded-full font-semibold border-neutral-200 dark:border-neutral-700"
              onClick={() => {
                document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })
              }}
            >
              See Pricing
            </Button>
          </div>
        </section>

        {/* Ask AI to Explain */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <AskAIExplain
            context="comparison"
            meta={{ competitorName: "InstantDM", pageUrl: "https://zapify.pro/comparison/instantdm-vs-zapify-pro" }}
            isCard
            isWidget
          />
        </section>

        {/* Section 2: Quick Answer Box */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20" aria-label="Featured summary">
          <article className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-widest text-brand-button mb-3">
              Bottom line - which is better, Zapify or InstantDM?
            </p>
            <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed mb-4">
              <strong className="text-neutral-900 dark:text-white">Zapify is the better choice for Instagram creators who want growth, not operational complexity.</strong>{" "}
              InstantDM offers scheduling, Facebook automation, API access, and viral mode - useful for agencies, but more tool than most creators need.
            </p>
            <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed">
              Choose Zapify if you want <strong className="gradient-text">AI replies</strong>, growth attribution, smart offers, templates, and a 30-day free plan. InstantDM is worth considering only if you need scheduling, Facebook support, or API/webhook integrations.
            </p>
          </article>
        </section>

        {/* Section 3: Problem With InstantDM */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              The problem with <span className="gradient-text">InstantDM</span> for creators
            </h2>
            <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto mt-3">
              InstantDM is feature-heavy. That is useful for agencies, but it creates unnecessary complexity for creators who want to grow.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {problemCards.map((item) => (
              <article key={item.title} className="rounded-xl border border-red-200 dark:border-red-800/40 bg-red-50/50 dark:bg-red-900/10 p-5 sm:p-6">
                <div className="w-9 h-9 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                  <X className="w-4 h-4 text-red-500 dark:text-red-400" strokeWidth={3} />
                </div>
                <h3 className="text-base font-bold text-neutral-900 dark:text-white mb-2">{item.title}</h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">{item.body}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Section 4: Choose Tool */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl border-2 border-green-200 dark:border-green-800/50 bg-white dark:bg-neutral-900/50 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <h2 className="text-lg font-bold text-green-600 dark:text-green-400">Choose Zapify if you…</h2>
              </div>
              <ul className="space-y-2.5">
                {chooseZapifyIf.map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    <span className="text-sm text-neutral-700 dark:text-neutral-300 leading-snug">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border-2 border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900/50 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
                </div>
                <h2 className="text-lg font-bold text-neutral-600 dark:text-neutral-400">Choose InstantDM only if you…</h2>
              </div>
              <ul className="space-y-2.5">
                {chooseInstantDMIf.map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    <span className="text-sm text-neutral-600 dark:text-neutral-400 leading-snug">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Section 5: Feature Comparison Table */}
        <section id="feature-table" className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              How does Zapify compare to InstantDM feature{" "}
              <span className="gradient-text">by feature?</span>
            </h2>
            <Paragraph className="max-w-3xl mx-auto">
              A transparent, side-by-side breakdown of every Instagram automation capability that matters in 2026.
            </Paragraph>
          </div>

          <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm bg-white dark:bg-neutral-900/50">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-neutral-50/80 dark:bg-neutral-900/80 border-b border-neutral-200 dark:border-neutral-800">
                    <th className="text-left px-4 sm:px-6 py-4 font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider text-xs w-[40%]">Feature</th>
                    <th className="text-left px-4 sm:px-6 py-4 uppercase tracking-wider text-xs w-[30%] text-green-700 dark:text-green-300">Zapify</th>
                    <th className="text-left px-4 sm:px-6 py-4 font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider text-xs w-[30%]">InstantDM</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {featureCategories.flatMap((cat) => [
                    <tr key={`cat-${cat.category}`} className="bg-neutral-50/70 dark:bg-neutral-900/70">
                      <td colSpan={3} className="px-4 sm:px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-brand-button">{cat.category}</td>
                    </tr>,
                    ...cat.rows.map((row) => (
                      <tr key={`${cat.category}-${row.name}`} className="hover:bg-neutral-50/50 dark:hover:bg-neutral-800/20 transition-colors align-top">
                        <td className="px-4 sm:px-6 py-3.5 font-medium text-neutral-700 dark:text-neutral-300">{row.name}</td>
                        <td className="px-4 sm:px-6 py-3.5">
                          <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">{typeof row.zapify === "boolean" ? (row.zapify ? "Yes" : "No") : row.zapify}</span>
                            {row.zapifyNote ? <span className="text-xs text-green-700 dark:text-green-300">{row.zapifyNote}</span> : null}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-3.5">
                          <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">{typeof row.instantdm === "boolean" ? (row.instantdm ? "Yes" : "No") : row.instantdm}</span>
                            {row.instantdmNote ? <span className="text-xs text-red-600 dark:text-red-300">{row.instantdmNote}</span> : null}
                          </div>
                        </td>
                      </tr>
                    )),
                  ])}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Section 6: Pricing Breakdown */}
        <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              Zapify vs InstantDM <span className="gradient-text">pricing</span>
            </h2>
            <Paragraph className="max-w-2xl mx-auto">
              Same starting price, but Zapify gives you 30 days free and lifetime access potential.
            </Paragraph>
          </div>

          <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-neutral-50/80 dark:bg-neutral-900/80 border-b border-neutral-200 dark:border-neutral-800">
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Plan stage</th>
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-green-700 dark:text-green-300">Zapify</th>
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-300">InstantDM</th>
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Why it matters</th>
                </tr>
              </thead>
              <tbody>
                {pricingTiers.map((tier) => (
                  <tr key={tier.label} className="align-top border-b last:border-b-0 border-neutral-100 dark:border-neutral-800/80">
                    <td className="px-4 sm:px-5 py-4 text-sm font-semibold text-neutral-800 dark:text-neutral-100">{tier.label}</td>
                    <td className="px-4 sm:px-5 py-4 text-lg font-black tracking-tight text-green-700 dark:text-green-300">{tier.zapify}</td>
                    <td className="px-4 sm:px-5 py-4 text-lg font-black tracking-tight text-neutral-700 dark:text-neutral-100">{tier.instantdm}</td>
                    <td className="px-4 sm:px-5 py-4"><span className="text-xs sm:text-sm text-green-700 dark:text-green-300 font-semibold">{tier.note}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8">
            <LifetimeAccessPricing
              spotsLeft={87}
              totalSpots={100}
              price={200}
              originalMonthlyPrice={39}
              onGetAccess={() => handleCTA("CTA_InstantDM_Lifetime_Pricing", "Claim Lifetime Access")}
            />
          </div>
        </section>

        {/* Section 7: Why Zapify Wins */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              What makes Zapify the better <span className="gradient-text">growth tool</span>
            </h2>
            <Paragraph className="max-w-2xl mx-auto">
              Six reasons Instagram-first creators choose Zapify over InstantDM.
            </Paragraph>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {differentiators.map((item) => (
              <article key={item.num} className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-5 sm:p-6">
                <div className="flex items-start gap-4">
                  <span className="text-2xl font-black text-brand-button/30 dark:text-brand-button/20 leading-none shrink-0">{item.num}</span>
                  <div>
                    <h3 className="font-bold text-neutral-900 dark:text-white text-sm mb-1.5">{item.title}</h3>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">{item.body}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Section 8: Honest Verdict */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <article className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight mb-4">
              Honest verdict: <span className="gradient-text">Zapify wins</span>
            </h2>
            <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed max-w-4xl mx-auto">
              InstantDM is a capable operations tool, but most Instagram creators do not need scheduling, API webhooks, or viral mode configs. Zapify is simpler, more focused, and built around the outcomes creators care about: more followers, more leads, better insights, and smarter AI replies.
            </p>
          </article>
        </section>

        {/* Section 9: FAQ */}
        <section className="w-full py-12 sm:py-20 px-4 sm:px-6 border-b border-neutral-100 dark:border-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8 text-center sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-[#181C31] dark:text-white tracking-tight">
                Frequently <span className="gradient-text">Asked Questions</span>
              </h2>
              <p className="text-sm sm:text-base md:text-xl text-[#64657E] dark:text-gray-400 font-medium max-w-2xl mx-auto mt-3 sm:mt-5">
                Everything you need to know about InstantDM vs Zapify
              </p>
            </div>

            <div className="mx-auto max-w-5xl space-y-3">
              {faqs.map((faq, index) => {
                const isOpen = openFaq === index
                return (
                  <div key={faq.question} className="rounded-2xl border border-border/50 bg-card/90 px-4 py-3.5 sm:px-6 sm:py-4 dark:bg-card/80">
                    <button type="button" onClick={() => setOpenFaq((prev) => (prev === index ? -1 : index))} className="flex w-full items-center justify-between gap-3 text-left">
                      <div className="flex items-center gap-3">
                        <span className="text-base leading-none">{["⚡", "💸", "🤖", "🔄", "📩", "🛡️", "🧠", "💬", "🎯"][index] ?? "⚡"}</span>
                        <p className="text-base font-semibold leading-snug text-foreground sm:text-[1.15rem]">{faq.question}</p>
                      </div>
                      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground">{isOpen ? "−" : "+"}</span>
                    </button>
                    <div className={`grid transition-all duration-300 ${isOpen ? "mt-2.5 grid-rows-[1fr] opacity-100 sm:mt-3" : "grid-rows-[0fr] opacity-0"}`}>
                      <p className="overflow-hidden pr-3 text-[13px] leading-relaxed text-muted-foreground sm:pr-8 sm:text-[0.95rem]">{faq.answer}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Section 10: Other Comparisons */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 border-b border-neutral-100 dark:border-white/5">
          <div className="text-center mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white">
              Compare Zapify vs <span className="gradient-text">other tools</span>
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: "Zapify vs ManyChat", href: "/comparison/manychat-vs-zapify-pro" },
              { label: "Zapify vs LinkDM", href: "/comparison/linkdm-vs-zapify-pro" },
              { label: "Zapify vs Zorcha", href: "/comparison/zorcha-vs-zapify-pro" },
              { label: "Zapify vs ReplyRush", href: "/comparison/replyrush-vs-zapify-pro" },
              { label: "Zapify vs SuperProfile", href: "/comparison/superprofile-vs-zapify-pro" },
            ].map((link) => (
              <a key={link.href} href={link.href} className="px-5 py-2.5 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 text-sm font-semibold text-neutral-700 dark:text-neutral-300 hover:border-neutral-300 dark:hover:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-all duration-200">
                {link.label}
              </a>
            ))}
          </div>
        </section>

        {/* Section 11: Bottom CTA */}
        <section className="relative overflow-hidden py-16 sm:py-24 px-4 sm:px-6">
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-brand-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-2xl mx-auto text-center">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-neutral-900 dark:text-white mb-4 sm:mb-6 tracking-tight">
              Simpler automation. <span className="gradient-text">Real growth.</span>
            </h2>
            <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400 mb-8 sm:mb-10 max-w-xl mx-auto">
              Start free for 30 days, launch your first automation in minutes, and let AI handle the rest.
            </p>
            <Button
              onClick={() => handleCTA("CTA_Comparison_Bottom", "Start Free with Zapify")}
              className="bg-primary-gradient text-primary-foreground hover:brightness-[0.97] active:brightness-[0.92] font-bold px-10 py-6 text-lg rounded-full shadow-lg"
            >
              Start Free with Zapify
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">
              30-day free plan &middot; No credit card &middot; 1-minute setup
            </p>
          </div>
        </section>

        {/* Disclaimer */}
        <ContentSection className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">
          <p className="text-xs text-neutral-400 dark:text-neutral-500 text-center">
            InstantDM is a trademark of its respective owner. Zapify is not affiliated with, endorsed by, or sponsored by InstantDM. Feature and pricing information is based on publicly available information and Zapify research as of June 6, 2026. Always check each provider's website for current pricing and plan details.
          </p>
        </ContentSection>
      </div>
    </div>
  )
}
